import React, { useEffect, useState, useMemo } from 'react';
import { bgColorClassname } from '@/utils';
import Cookies from 'universal-cookie';
import { graphql, useStaticQuery } from 'gatsby';
import type { PortableTextBlock as PortableTextBlockType } from '@sanity/types';
import { Button } from '@/components/ui/button';
import { Pagination, DetailCard } from '@/components';

interface PressListingComponentProps {
  backgroundColor?: {
    label: string;
    value: string;
  };
}

interface PressItem {
  _id: string;
  _type: string;
  title: string;
  shortTitle?: string;
  language: string;
}

interface SanityPressRelease extends PressItem {
  slug: {
    current: string;
  };
  content: PortableTextBlockType;
  date: string;
}

interface SanityMediaCoverage extends PressItem {
  externalUrl: string;
  abstract: string;
  publicationDate: string;
}

interface PressMediaListingQueryResult {
  allSanityPressRelease: {
    nodes: SanityPressRelease[];
  };
  allSanityMediaCoverage: {
    nodes: SanityMediaCoverage[];
  };
}

const PRESS_MEDIA_LISTING_QUERY = graphql`
  query PressMediaListing {
    allSanityPressRelease {
      nodes {
        _id
        _type
        title
        shortTitle
        slug {
          current
        }
        language
        date
        content: _rawContent
      }
    }
    allSanityMediaCoverage {
      nodes {
        _id
        _type
        title
        shortTitle
        externalUrl
        language
        abstract
        publicationDate
      }
    }
  }
`;

const PressListing: React.FC<PressListingComponentProps> = ({ backgroundColor }) => {
  const [filteredResults, setFilteredResults] = useState<
    (SanityPressRelease | SanityMediaCoverage)[]
  >([]);
  const [hasNoResults, setHasNoResults] = useState(false);
  const [selectedContentType, setSelectedContentType] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  const ITEMS_PER_PAGE = 15;

  const contentTypeFilters = [
    { value: 'pressRelease', label: 'press release' },
    { value: 'mediaCoverage', label: 'media coverage' },
  ];

  const cookies = new Cookies();
  const preferredLanguage = cookies.get('preferredLanguage') || 'en';

  const data: PressMediaListingQueryResult = useStaticQuery(PRESS_MEDIA_LISTING_QUERY);

  // Merge press release and media coverage data with consistent `date` field
  const mergedContent = useMemo(() => {
    const merged = [
      ...data.allSanityPressRelease.nodes.map((node) => ({
        ...node,
        content: Array.isArray(node.content) ? node.content.slice(0, 1) : [],
      })),
      ...data.allSanityMediaCoverage.nodes.map((node) => ({
        ...node,
        date: node.publicationDate,
      })),
    ];

    // Sort by date in descending order (most recent first)
    return merged.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [data]);

  // Apply filtering based on selected content type and language
  useEffect(() => {
    let filtered = mergedContent;

    if (selectedContentType) {
      filtered = filtered.filter((item) => item._type === selectedContentType);
    }

    if (preferredLanguage) {
      filtered = filtered.filter((item) => item.language === preferredLanguage);
    }

    setFilteredResults(filtered);
    setHasNoResults(filtered.length === 0);
  }, [selectedContentType, preferredLanguage, mergedContent]);

  const handleContentTypeSelection = (type: string | null) => {
    setSelectedContentType(type);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const paginatedResults = useMemo(
    () => filteredResults.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE),
    [filteredResults, currentPage, ITEMS_PER_PAGE],
  );

  const totalItems = filteredResults.length;
  const shownItems = paginatedResults.length;

  return (
    <div className={backgroundColor ? bgColorClassname(backgroundColor) : ''}>
      <div className='mx-auto max-w-screen-xl px-5 py-8 md:gap-x-6 md:p-[50px] lg:gap-x-12 xl:gap-x-24'>
        {/* Filter Tabs */}
        <div className='mb-6 flex flex-wrap-reverse items-center justify-between gap-4 border-b-2 border-zinc-300 pb-6 md:flex-nowrap md:gap-3'>
          <p className='text-base font-light leading-5 text-zinc-700'>filter by</p>
          <div className='flex w-full flex-1 flex-wrap gap-2 lowercase md:w-auto'>
            <Button
              variant={!selectedContentType ? 'default' : 'outline'}
              size='sm'
              onClick={() => handleContentTypeSelection(null)}
            >
              all
            </Button>

            {contentTypeFilters.map((type) => (
              <Button
                key={type.value}
                variant={selectedContentType === type.value ? 'default' : 'outline'}
                size='sm'
                onClick={() => handleContentTypeSelection(type.value)}
              >
                {type.label}
              </Button>
            ))}
          </div>
        </div>

        {/* Dynamic Total and Shown Items */}
        {filteredResults.length > 0 && (
          <p className='mb-5 text-base font-medium leading-5'>
            {shownItems} of {totalItems} items shown
          </p>
        )}

        {/* Results */}
        {!hasNoResults && (
          <div className='grid grid-cols-1 gap-10 md:grid-cols-2 lg:grid-cols-3'>
            {paginatedResults.map((result) => (
              <DetailCard
                key={result._id}
                result={result}
              />
            ))}
          </div>
        )}

        {/* Pagination */}
        {filteredResults.length > ITEMS_PER_PAGE && (
          <Pagination
            currentPage={currentPage}
            totalItems={totalItems}
            itemsPerPage={ITEMS_PER_PAGE}
            onPageChange={handlePageChange}
          />
        )}

        {hasNoResults && (
          <div className='text-center text-xl font-bold text-gray-500'>no results found</div>
        )}
      </div>
    </div>
  );
};

export default PressListing;
